import { useState, useEffect } from "react";
import { Plus, X, Folder } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import {
	Drawer,
	DrawerContent,
	DrawerDescription,
	DrawerHeader,
	DrawerTitle,
	DrawerTrigger,
} from "@/components/ui/drawer";
import {
	PodWorkspaceService,
	type PodWorkspace,
} from "@/lib/podWorkspace/podWorkspace.service";
import { useTranslation } from "react-i18next";

interface WorkspaceSelectorProps {
	currentWorkspaceId?: string;
	selectedWorkspaceIds: string[];
	onSelectionChange: (workspaceIds: string[]) => void;
	onSave?: (workspaceIds: string[]) => void;
	allowPrimarySelection?: boolean;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
}

/**
 * WorkspaceSelector component
 * Allows users to select multiple workspaces for combined chat context
 * Mobile-friendly with bottom sheet (Drawer)
 */
export function WorkspaceSelector({
	currentWorkspaceId,
	selectedWorkspaceIds,
	onSelectionChange,
	onSave,
	allowPrimarySelection = false,
	open: controlledOpen,
	onOpenChange: controlledOnOpenChange,
}: WorkspaceSelectorProps) {
	const { t } = useTranslation();
	const [workspaces, setWorkspaces] = useState<PodWorkspace[]>([]);
	const [filteredWorkspaces, setFilteredWorkspaces] = useState<PodWorkspace[]>(
		[],
	);
	const [isLoading, setIsLoading] = useState(true);
	const [internalOpen, setInternalOpen] = useState(false);

	// Use controlled state if provided, otherwise use internal state
	const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
	const setIsOpen = controlledOnOpenChange || setInternalOpen;
	const [searchQuery, setSearchQuery] = useState("");

	// Track pending selections locally (only apply on save)
	const [pendingSelections, setPendingSelections] =
		useState<string[]>(selectedWorkspaceIds);

	// Sync pending selections when selector opens or selectedWorkspaceIds changes externally
	useEffect(() => {
		if (isOpen) {
			setPendingSelections(selectedWorkspaceIds);
		}
	}, [isOpen, selectedWorkspaceIds]);

	// Fetch ALL user's workspaces
	useEffect(() => {
		const fetchWorkspaces = async () => {
			try {
				setIsLoading(true);
				const allWorkspaces =
					await PodWorkspaceService.getAllWorkspacesForUser();

				setWorkspaces(allWorkspaces);
				setFilteredWorkspaces(allWorkspaces);
			} catch (error) {
				console.error("Failed to fetch workspaces:", error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchWorkspaces();
	}, []);

	// Filter workspaces based on search query
	useEffect(() => {
		if (!searchQuery.trim()) {
			setFilteredWorkspaces(workspaces);
		} else {
			const query = searchQuery.toLowerCase();
			const filtered = workspaces.filter((w) =>
				w.title.toLowerCase().includes(query),
			);
			setFilteredWorkspaces(filtered);
		}
	}, [searchQuery, workspaces]);

	// Get current workspace
	const currentWorkspace = workspaces.find((w) => w.id === currentWorkspaceId);

	// Get additional selected workspaces (excluding current workspace)
	const additionalWorkspaces = workspaces.filter(
		(w) => selectedWorkspaceIds.includes(w.id) && w.id !== currentWorkspaceId,
	);

	// Toggle workspace selection (updates local pending state, not applied until save)
	const toggleWorkspace = (workspaceId: string) => {
		if (pendingSelections.includes(workspaceId)) {
			setPendingSelections(
				pendingSelections.filter((id) => id !== workspaceId),
			);
		} else {
			setPendingSelections([...pendingSelections, workspaceId]);
		}
	};

	// Remove a selected workspace (updates local pending state)
	const removeWorkspace = (workspaceId: string, e: React.MouseEvent) => {
		e.stopPropagation();
		setPendingSelections(pendingSelections.filter((id) => id !== workspaceId));
	};

	// Select all workspaces (filtered) - updates local pending state
	const selectAll = () => {
		setPendingSelections(filteredWorkspaces.map((w) => w.id));
	};

	// Clear all selections except current workspace (which is always included) - updates local pending state
	const clearAll = () => {
		if (currentWorkspaceId) {
			setPendingSelections([currentWorkspaceId]);
		} else {
			setPendingSelections([]);
		}
	};

	// Handle save - apply pending selections
	const handleSave = () => {
		if (onSave) {
			onSave(pendingSelections);
		} else {
			// Fallback to onSelectionChange if onSave not provided
			onSelectionChange(pendingSelections);
			setIsOpen(false);
		}
	};

	return (
		<div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-border">
			{/* Show current workspace badge or prompt to select one */}
			{currentWorkspace ? (
				<Badge
					variant="secondary"
					className="flex items-center gap-1.5 pl-2 pr-2 py-1 text-xs max-w-[200px]"
				>
					<Folder className="h-3 w-3 flex-shrink-0" />
					<span className="truncate flex-1">{currentWorkspace.title}</span>
				</Badge>
			) : allowPrimarySelection ? (
				<Badge
					variant="outline"
					className="flex items-center gap-1.5 pl-2 pr-2 py-1 text-xs text-muted-foreground"
				>
					<Folder className="h-3 w-3 flex-shrink-0" />
					<span className="truncate flex-1">
						{t("chat.workspaceSelector.selectWorkspace")}
					</span>
				</Badge>
			) : null}

			{/* Show additional selected workspaces as removable badges */}
			{additionalWorkspaces.map((workspace) => (
				<Badge
					key={workspace.id}
					variant="secondary"
					className="flex items-center gap-1.5 pl-2 pr-1 py-1 text-xs max-w-[200px]"
				>
					<Folder className="h-3 w-3 flex-shrink-0" />
					<span className="truncate flex-1">{workspace.title}</span>
					<button
						type="button"
						onClick={(e) => removeWorkspace(workspace.id, e)}
						className="hover:bg-muted rounded-full p-0.5 transition-colors"
						aria-label={t("chat.workspaceSelector.remove")}
					>
						<X className="h-3 w-3" />
					</button>
				</Badge>
			))}

			{/* Add Workspace Button */}
			<Drawer
				open={isOpen}
				onOpenChange={(open) => {
					if (!open) {
						// Reset pending selections when closing without saving
						setPendingSelections(selectedWorkspaceIds);
					}
					setIsOpen(open);
				}}
			>
				<DrawerTrigger asChild>
					<Button
						variant="outline"
						size="sm"
						className="h-7 text-xs gap-1.5"
						disabled={isLoading || workspaces.length === 0}
					>
						<Plus className="h-3.5 w-3.5" />
						{!currentWorkspaceId && allowPrimarySelection
							? t("chat.workspaceSelector.selectWorkspace")
							: t("chat.workspaceSelector.addWorkspace")}
					</Button>
				</DrawerTrigger>
				<DrawerContent
					className="h-[80vh] md:h-[60vh] flex flex-col z-[80]"
					overlayClassName="z-[79]"
				>
					<DrawerHeader className="px-4 py-4 border-b border-border">
						<DrawerTitle>
							{!currentWorkspaceId && allowPrimarySelection
								? t("chat.workspaceSelector.selectPrimaryWorkspace")
								: t("chat.workspaceSelector.selectWorkspaces")}
						</DrawerTitle>
						<DrawerDescription>
							{!currentWorkspaceId && allowPrimarySelection
								? t("chat.workspaceSelector.selectPrimaryDescription")
								: t("chat.workspaceSelector.selectDescription")}
						</DrawerDescription>
					</DrawerHeader>

					{/* Search and Quick Actions */}
					<div className="flex flex-col gap-2 px-4 py-3 border-b border-border">
						<Input
							type="text"
							placeholder={t("chat.workspaceSelector.searchPlaceholder")}
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
							className="h-9"
						/>
						<div className="flex gap-2">
							<Button
								variant="outline"
								size="sm"
								onClick={selectAll}
								disabled={
									filteredWorkspaces.length === 0 ||
									selectedWorkspaceIds.length === filteredWorkspaces.length
								}
							>
								{t("chat.workspaceSelector.selectAll")}
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={clearAll}
								disabled={selectedWorkspaceIds.length <= 1}
							>
								{t("chat.workspaceSelector.clearAll")}
							</Button>
						</div>
					</div>

					{/* Workspace List */}
					<ScrollArea className="flex-1 min-h-0 px-4">
						{isLoading ? (
							<div className="flex items-center justify-center py-8">
								<div className="flex items-center gap-2 text-muted-foreground">
									<div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
									<span className="text-sm">{t("common.loading")}</span>
								</div>
							</div>
						) : filteredWorkspaces.length === 0 ? (
							<div className="flex flex-col items-center justify-center py-8 text-center">
								<Folder className="h-12 w-12 text-muted-foreground mb-3" />
								<p className="text-sm text-muted-foreground">
									{searchQuery.trim()
										? t("chat.workspaceSelector.noResults")
										: t("chat.workspaceSelector.noWorkspaces")}
								</p>
							</div>
						) : (
							<div className="space-y-2 py-4">
								{filteredWorkspaces.map((workspace) => {
									const isSelected = pendingSelections.includes(workspace.id);
									const isCurrentWorkspace =
										workspace.id === currentWorkspaceId;

									return (
										<button
											key={workspace.id}
											type="button"
											onClick={() =>
												!isCurrentWorkspace && toggleWorkspace(workspace.id)
											}
											disabled={isCurrentWorkspace}
											className={`w-full flex items-start gap-3 p-3 rounded-lg border border-border transition-colors text-left ${
												isCurrentWorkspace
													? "opacity-60 cursor-not-allowed"
													: "hover:bg-accent"
											}`}
										>
											<Checkbox
												checked={isSelected}
												onCheckedChange={() =>
													!isCurrentWorkspace && toggleWorkspace(workspace.id)
												}
												disabled={isCurrentWorkspace}
												className="mt-0.5"
											/>
											<div className="flex-1 min-w-0">
												<div className="flex items-center gap-2 mb-1">
													<div className="font-medium text-sm line-clamp-2 flex-1">
														{workspace.title}
													</div>
													{isCurrentWorkspace && (
														<Badge
															variant="secondary"
															className="text-xs px-1.5 py-0 h-5 flex-shrink-0"
														>
															{t("chat.workspaceSelector.current")}
														</Badge>
													)}
												</div>
											</div>
											<Folder className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
										</button>
									);
								})}
							</div>
						)}
					</ScrollArea>

					{/* Footer with selection count and save button */}
					<div className="px-4 py-3 border-t border-border bg-muted/30">
						<div className="flex items-center justify-between text-sm gap-2">
							<span className="text-muted-foreground">
								{t("chat.workspaceSelector.selectedCount", {
									count: pendingSelections.length,
								})}
							</span>
							<div className="flex gap-2">
								<Button
									variant="outline"
									onClick={() => {
										// Reset pending selections when closing without saving
										setPendingSelections(selectedWorkspaceIds);
										setIsOpen(false);
									}}
								>
									{t("common.close")}
								</Button>
								<Button onClick={handleSave}>{t("common.save")}</Button>
							</div>
						</div>
					</div>
				</DrawerContent>
			</Drawer>
		</div>
	);
}
